import React from 'react';
import { Box, Typography, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Link } from '@mui/material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import MSTeam from '../../assets/MSTeam-Settings.png';
import ManageMap from '../../assets/ManageMap.jpg';
import Corona from '../../assets/Corona.png';

const primaryColor = '#4c43a3';

const UserGuide = () => {
    const Manager = [
        'Manage users',
        'Configure repositories',
        'Enable Continuous Learning',
        'Purchase and upgrade plans',
        'Monitor analytics',
    ];
    const User = ['Upload SCORM', 'View extracted content'];
    const dashboard = [
        'Total courses processed',
        'Storage usage',
        'Extraction success rate',
        'Media duration tracking',
        'Subscription validity',
    ];

    const formats = ['SCORM 1.2', 'SCORM 2004'];
    const system = ['File size', 'Manifest structure', 'Package integrity'];
    const outputs = ['Structured PDF, MD & JSON format', 'Media transcripts', 'Metadata'];
    const rules = [
        '7-question limit',
        'Course-only knowledge',
        'Friendly conversational support',
        'Auto-disable after limit',
        'Chat history deleted after trial expiry',
    ];
    const repository = [
        'Connect cloud storage',
        'Create upload batches',
        'Monitor real-time extraction',
        'Manage large-scale migrations',
    ];
    const reports = ['Upload activity', 'Extraction success', 'Storage utilization', 'Media consumption', 'API usage'];
    const integrations = ['LMS platforms', 'AI tools', 'Enterprise systems', 'Developer APIs'];
    const help = [
        { title: 'Upload failed?', description: 'Check file size & manifest' },
        { title: 'AI not responding?', description: 'Verify API key' },
        { title: 'Trial expired?', description: 'Upgrade your plan' },
    ];

    return (
        <Box sx={{ fontFamily: 'Inter, sans-serif', color: '#000000' }}>
            {/* Header Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#000000' }}>
                    About the Platform
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                    Xtract2AI is a multi-tenant, AI-first SCORM extraction platform built to modernize legacy eLearning
                    content. It converts SCORM packages into structured formats and enables AI-powered learning experiences
                    across systems.
                </Typography>
            </Box>
            {/* User Roles */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#000000' }}>
                    User Roles
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #e0e0e0',
                            transition: 'box-shadow 0.3s',
                            '&:hover': { boxShadow: '0 8px 30px rgba(76, 67, 163, 0.15)' },
                        }}
                    >
                        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                            <Box>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        bgcolor: `${primaryColor}20`,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        mb: 2,
                                    }}
                                >
                                    <SupervisorAccountIcon sx={{ color: primaryColor, fontSize: 32 }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
                                    Organisational Manager
                                </Typography>
                                <List sx={{ py: 0 }}>
                                    {Manager.map((audience, index) => (
                                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <CheckCircleIcon sx={{ color: primaryColor }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={audience}
                                                sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </CardContent>
                    </Card>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #e0e0e0',
                            transition: 'box-shadow 0.3s',
                            '&:hover': { boxShadow: '0 8px 30px rgba(76, 67, 163, 0.15)' },
                        }}
                    >
                        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                            <Box>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        bgcolor: `${primaryColor}20`,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        mb: 2,
                                    }}
                                >
                                    <PersonIcon sx={{ color: primaryColor, fontSize: 32 }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
                                    Member
                                </Typography>
                                {User.map((audience, index) => (
                                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={audience}
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Dashboard Overview */}
            <Box sx={{ bgcolor: '#f8f9fa', p: 4, borderRadius: 3, mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Dashboard Overview
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 4 }}>
                    Your dashboard provides
                </Typography>
                <List
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 2,
                    }}
                >
                    {dashboard.map((feature, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                padding: '16px 20px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                background: '#fff',
                                color: '#000',
                            }}
                        >
                            <ListItemText
                                primary={feature}
                                sx={{
                                    textAlign: 'center',
                                    '& .MuiListItemText-primary': { color: '#000', fontWeight: 500 },
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>

            {/* SCORM Extraction Process */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 4, color: '#000000' }}>
                    SCORM Extraction Process
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 4,
                    }}
                >
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #e0e0e0',
                            height: '100%',
                        }}
                    >
                        <CardContent sx={{ px: 4, py: 3 }}>
                            <Typography variant="body1" sx={{ color: '#6a677e' }}>
                                Supported formats:
                            </Typography>
                            <List sx={{ p: 0, mt: 1 }}>
                                {formats.map((feature, index) => (
                                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={feature}
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #e0e0e0',
                            height: '100%',
                        }}
                    >
                        <CardContent sx={{ px: 4, py: 3 }}>
                            <Typography variant="body1" sx={{ color: '#6a677e' }}>
                                Validation checks:
                            </Typography>
                            <List sx={{ p: 0, mt: 1 }}>
                                {system.map((feature, index) => (
                                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={feature}
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #e0e0e0',
                            height: '100%',
                        }}
                    >
                        <CardContent sx={{ px: 4, py: 3 }}>
                            <Typography variant="body1" sx={{ color: '#6a677e' }}>
                                Extracted outputs:
                            </Typography>
                            <List sx={{ p: 0, mt: 1 }}>
                                {outputs.map((feature, index) => (
                                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={feature}
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* AI Assistant */}
            <Box sx={{ bgcolor: '#f8f9fa', p: 4, borderRadius: 3, mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 4, color: '#000000' }}>
                    AI Assistant – xTra (Trial Users)
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 4 }}>
                    xTra helps you interact with your course content using AI. Available only after extraction completion.
                </Typography>
                <Card
                    sx={{
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0',
                        height: '100%',
                    }}
                >
                    <CardContent sx={{ px: 4, py: 3 }}>
                        <Typography variant="body1" sx={{ color: '#6a677e' }}>
                            Key Rules
                        </Typography>
                        <List sx={{ p: 0, display: 'flex', flexWrap: 'wrap', mt: 1 }}>
                            {rules.map((feature, index) => (
                                <ListItem
                                    key={index}
                                    sx={{ px: 0, py: 0.5, flexBasis: { xs: '100%', sm: '50%', md: '33.33%' } }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: primaryColor }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={feature}
                                        sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mt: 2 }}>
                    Designed to help you experience AI-powered learning before subscribing.
                </Typography>
            </Box>

            {/* Continuous Learning (Enterprise AI) */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Continuous Learning (Enterprise AI)
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', mb: 4, textAlign: 'center' }}>
                    Continuous Learning connects your extracted SCORM content to enterprise AI systems like
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 4,
                    }}
                >
                    <img
                        src={MSTeam}
                        alt="MS Teams"
                        style={{
                            width: '100%',
                            height: 'auto',
                            border: '1px solid #e0e0e0',
                            objectFit: 'contain',
                            borderRadius: 8,
                        }}
                    />
                    <img
                        src={ManageMap}
                        alt="Manage Map"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: '1px solid #e0e0e0',
                            objectFit: 'contain',
                            borderRadius: 8,
                        }}
                    />
                    <img
                        src={Corona}
                        alt="Corona"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: '1px solid #e0e0e0',
                            objectFit: 'contain',
                            borderRadius: 8,
                        }}
                    />
                </Box>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mt: 3 }}>
                    This is where SCORM becomes AI intelligence.
                </Typography>
            </Box>

            <Box sx={{ bgcolor: '#f8f9fa', p: 4, borderRadius: 3, mb: 4 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.33% - 16px)' } }}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e0e0e0',
                                transition: 'box-shadow 0.3s',
                                '&:hover': { boxShadow: '0 8px 30px rgba(76, 67, 163, 0.15)' },
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
                                    Repository & Batch Processing
                                </Typography>
                                <List sx={{ p: 0, mt: 1 }}>
                                    {repository.map((feature, index) => (
                                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <CheckCircleIcon sx={{ color: primaryColor }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={feature}
                                                sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.33% - 16px)' } }}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e0e0e0',
                                transition: 'box-shadow 0.3s',
                                '&:hover': { boxShadow: '0 8px 30px rgba(76, 67, 163, 0.15)' },
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
                                    Reports & Analytics
                                </Typography>
                                <List sx={{ p: 0, mt: 1 }}>
                                    {reports.map((feature, index) => (
                                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <CheckCircleIcon sx={{ color: primaryColor }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={feature}
                                                sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.33% - 16px)' } }}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e0e0e0',
                                transition: 'box-shadow 0.3s',
                                '&:hover': { boxShadow: '0 8px 30px rgba(76, 67, 163, 0.15)' },
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
                                    Integrations
                                </Typography>
                                <List sx={{ p: 0, mt: 1 }}>
                                    {integrations.map((feature, index) => (
                                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <CheckCircleIcon sx={{ color: primaryColor }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={feature}
                                                sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Box>

            {/* Need Help? */}
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Need Help?
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    {help.map((feature, index) => (
                        <Card
                            key={index}
                            sx={{
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e0e0e0',
                                transition: 'box-shadow 0.3s',
                                '&:hover': { boxShadow: '0 8px 30px rgba(76, 67, 163, 0.15)' },
                            }}
                        >
                            <CardContent
                                sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000', mb: 1 }}>
                                    {feature.title}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.5 }}>
                                    {feature.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
                <Typography variant="body1" sx={{ color: '#6a677e', textAlign: 'center', mt: 2 }}>
                    Support:
                    <Link href="mailto:support@xtract2ai.com" sx={{ color: primaryColor }}>
                        support@xtract2ai.com
                    </Link>
                </Typography>
            </Box>
        </Box>
    );
};

export default UserGuide;
